import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { FolderOpen, ArrowRight, Image as ImageIcon } from "lucide-react";
import { fetchPageById, fetchGalleries } from "../lib/cms";
import { getOptimizedImageUrl } from "../lib/utils";
import Lightbox from "../components/Lightbox";

export default function Projects() {
  const [page, setPage] = useState(null);
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGallery, setSelectedGallery] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [activeFilter, setActiveFilter] = useState("alle");

  useEffect(() => {
    async function loadProjectsData() {
      setLoading(true);
      const pageData = await fetchPageById("tidligere-projekter");
      setPage(pageData);

      const galleriesData = await fetchGalleries();
      setGalleries(galleriesData);
      setLoading(false);
    }
    loadProjectsData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-brand-200 border-t-brand-600" />
      </div>
    );
  }

  const title = page?.title?.da || "Tidligere projekter";
  const contentHtml = page?.content?.da || "";
  const metaTitle = page?.meta_title?.da || "Tidligere projekter - Sehested Tømrerfirma ApS";
  const metaDesc = page?.meta_description?.da || "Se vores tidligere projekter og referencer indenfor tømrerarbejde, renovering, tagarbejde, døre, vinduer og træterrasser.";

  // Filter galleries
  const filteredGalleries = galleries.filter((g) => {
    if (activeFilter === "alle") return true;
    if (activeFilter === "special") return g.gallery_id === "horn-vinduesfabrik";
    if (activeFilter === "renovering") return g.gallery_id === "renovering" || g.gallery_id === "til-og-ombygning";
    if (activeFilter === "tag") return g.gallery_id === "tagarbejde";
    if (activeFilter === "terrasse") return g.gallery_id === "traeterrasse";
    if (activeFilter === "vinduer") return g.gallery_id === "doere-og-vinduer" || g.gallery_id === "horn-vinduesfabrik";
    return true;
  });

  const openLightbox = (gallery, index) => {
    setSelectedGallery(gallery);
    setLightboxIndex(index);
  };

  return (
    <div className="bg-primary-50/30 py-12 md:py-20">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
      </Helmet>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto space-y-4 mb-12">
          <span className="text-sm font-bold uppercase tracking-widest text-brand-600">Vores arbejde</span>
          <p className="text-primary-600 text-lg">
            Vi udfører en bred vifte af opgaver – se vores seneste projekter her.
          </p>
        </div>

        {/* Page Content Intro */}
        {contentHtml && (
          <div className="bg-white rounded-2xl border border-primary-100 p-6 md:p-8 shadow-sm mb-12 max-w-7xl">
            <div
              className="rich-text"
              dangerouslySetInnerHTML={{ __html: contentHtml }}
            />
          </div>
        )}

        {/* Filter Controls */}
        <div className="flex flex-wrap items-center justify-center gap-2 mb-12">
          {[
            { id: "alle", label: "Alle projekter" },
            { id: "renovering", label: "Renovering & Ombygning" },
            { id: "tag", label: "Tagarbejde" },
            { id: "terrasse", label: "Træterrasser" },
            { id: "vinduer", label: "Døre & Vinduer" },
            { id: "special", label: "Specialprojekter" },
          ].map((filter) => (
            <button
              key={filter.id}
              onClick={() => setActiveFilter(filter.id)}
              className={`rounded-lg px-4 py-2 text-sm font-semibold transition-all duration-200 cursor-pointer ${
                activeFilter === filter.id
                  ? "bg-brand-600 text-white shadow-sm"
                  : "bg-white text-primary-700 border border-primary-200 hover:border-brand-500 hover:text-brand-600"
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>

        {/* Galleries Grid */}
        <div className="space-y-16">
          {filteredGalleries.map((gallery) => {
            const isSpecialProject = gallery.gallery_id === "horn-vinduesfabrik";
            return (
              <div
                key={gallery.gallery_id}
                className="bg-white rounded-2xl border border-primary-100 p-6 md:p-8 shadow-sm space-y-6"
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-primary-100 pb-4">
                  <div className="flex items-center gap-3">
                    <FolderOpen className="h-6 w-6 text-brand-600" />
                    <h2 className="font-display text-2xl font-bold text-primary-950">
                      {gallery.title?.da || gallery.title}
                    </h2>
                  </div>
                  {isSpecialProject && (
                    <Link
                      to="/horn-vinduesfabrik"
                      className="inline-flex items-center gap-1.5 text-sm font-bold text-brand-700 hover:text-brand-800 group"
                    >
                      Læs projektbeskrivelse
                      <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                    </Link>
                  )}
                </div>

                {/* Images Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {gallery.images?.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => openLightbox(gallery, index)}
                      className="group relative aspect-[4/3] overflow-hidden rounded-xl border border-primary-200/60 bg-primary-50 shadow-sm hover:border-brand-500 transition-all duration-300 cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-500"
                    >
                      <img
                        src={getOptimizedImageUrl(img, { width: 300, height: 225 })}
                        alt={`${gallery.title?.da || gallery.title} ${index + 1}`}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-black/10 group-hover:bg-black/0 transition-colors duration-300" />
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Lightbox */}
      {selectedGallery && (
        <Lightbox
          images={selectedGallery.images}
          currentIndex={lightboxIndex}
          onClose={() => {
            setSelectedGallery(null);
            setLightboxIndex(null);
          }}
          onPrev={() => setLightboxIndex((prev) => (prev - 1 + selectedGallery.images.length) % selectedGallery.images.length)}
          onNext={() => setLightboxIndex((prev) => (prev + 1) % selectedGallery.images.length)}
        />
      )}
    </div>
  );
}
